import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material"
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"

// Presentational join screen for /incidents/invite (M4.1). This is a
// first-touch moment for external invitees — legal counsel, outside IR — so it
// has to feel calm, credible, and zero-confusion: one heading, the channel
// context (when known), one prominent action. No app chrome (the route lives
// outside AppLayout), no incident name (the invite email intentionally
// withholds it), no secondary actions to second-guess.
//
// All colors come from MUI theme tokens so the screen is safe under both the
// DARK_THEME and LIGHT_THEME palettes — no hardcoded hexes, no
// gameplay-refactor CSS vars (that stylesheet is scoped to the incident detail
// page and is not loaded here).
//
// State is fully lifted: the parent (IncidentInviteHandler) owns the join
// mutation and the post-success navigation timing; this component only
// renders `{ joining, joined, error }`.
const IncidentJoinScreen = ({ channelNames = [], invitedEmail, onJoin, joining = false, joined = false, error = null }) => {
  const hasChannels = Array.isArray(channelNames) && channelNames.length > 0
  const plural = hasChannels && channelNames.length !== 1

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "center",
        px: 2,
        py: { xs: 6, sm: 4 },
        bgcolor: "background.default",
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 480,
          p: { xs: 3, sm: 4 },
          textAlign: "center",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            mx: "auto",
            mb: 2,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "action.hover",
            color: joined ? "success.main" : "primary.main",
          }}
        >
          {joined ? <CheckCircleOutlineIcon sx={{ fontSize: 30 }} /> : <ShieldOutlinedIcon sx={{ fontSize: 30 }} />}
        </Box>

        <Typography variant="overline" sx={{ letterSpacing: "0.12em", color: "text.secondary", display: "block", mb: 0.5 }}>
          Incident Response
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {joined ? "You're in" : "You've been invited"}
        </Typography>

        {joined ? (
          <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
            <Typography variant="body1" color="text.secondary">
              You're in — taking you to the incident…
            </Typography>
            <CircularProgress size={22} aria-label="Redirecting" />
          </Stack>
        ) : (
          <>
            {hasChannels ? (
              <>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  You've been asked to join the following response channel{plural ? "s" : ""}:
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", justifyContent: "center", mb: 3 }}>
                  {channelNames.map((name, index) => (
                    <Chip key={`${name}-${index}`} label={name} variant="outlined" color="primary" sx={{ fontWeight: 600 }} />
                  ))}
                </Stack>
              </>
            ) : (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                You've been invited to respond to an incident. Your channels will appear once you join.
              </Typography>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={onJoin}
              disabled={joining}
              startIcon={joining ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ fontWeight: 700, textTransform: "none", py: 1.25 }}
            >
              {joining ? "Joining…" : hasChannels && plural ? "Join Channels" : hasChannels ? "Join Channel" : "Join Incident"}
            </Button>

            {invitedEmail && (
              <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
                Invited as <strong>{invitedEmail}</strong>
              </Typography>
            )}
          </>
        )}
      </Paper>
    </Box>
  )
}

export default IncidentJoinScreen
